import type { StateCreator } from "zustand";
import type { NewsFeedDynamicSidebarFilterItem } from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";

export type DynamicFilterItem = NewsFeedDynamicSidebarFilterItem;

export type DynamicFilterCategoryData = {
  // Current data
  items: DynamicFilterItem[];

  // Future pagination support (optional fields)
  itemsMap?: Map<string, DynamicFilterItem>; // For O(1) lookups when needed
  pagination?: {
    hasNextPage: boolean;
    endCursor: string | null;
    totalCount: number;
  };

  // Loading states
  isLoading: boolean;
  isLoadingMore?: boolean; // For future pagination
  error: string | null;
};

export type DynamicFiltersSlice = {
  dynamicFilters: Map<string, DynamicFilterCategoryData>;

  // Current actions (work with your current query)
  setDynamicLoading: (category: string, isLoading: boolean) => void;
  updateDynamicItems: (category: string, items: DynamicFilterItem[]) => void;
  setDynamicError: (category: string, error: string | null) => void;
  removeDynamicCategory: (category: string) => void;
  clearAllDynamicFilters: () => void;

  // Future pagination actions
  setDynamicPage?: (
    category: string,
    items: DynamicFilterItem[],
    pagination: DynamicFilterCategoryData["pagination"],
  ) => void;
  appendDynamicItems?: (
    category: string,
    items: DynamicFilterItem[],
    pagination: DynamicFilterCategoryData["pagination"],
  ) => void;
  setDynamicLoadingMore?: (category: string, isLoading: boolean) => void;
};

/**
 * Keeps one dynamic filter row per ID while preserving first-seen order.
 */
export const normalizeDynamicFilterItems = (
  items: DynamicFilterItem[],
): DynamicFilterItem[] => {
  const itemsById = new Map<string, DynamicFilterItem>();
  const orderedIds: string[] = [];

  for (const item of items) {
    const existing = itemsById.get(item.id);

    if (!existing) {
      itemsById.set(item.id, item);
      orderedIds.push(item.id);
      continue;
    }

    if (item.total > existing.total) {
      itemsById.set(item.id, {
        ...existing,
        total: item.total,
      });
    }
  }

  const normalizedItems: DynamicFilterItem[] = [];
  for (const id of orderedIds) {
    const item = itemsById.get(id);
    if (item) {
      normalizedItems.push(item);
    }
  }

  return normalizedItems;
};

export const createDynamicFiltersSlice: StateCreator<
  DynamicFiltersSlice,
  [["zustand/devtools", never]],
  [],
  DynamicFiltersSlice
> = (set, get) => ({
  dynamicFilters: new Map(),

  // Current implementation
  setDynamicLoading: (category: string, isLoading: boolean) => {
    const currentFilters = get().dynamicFilters;
    const newFilters = new Map(currentFilters);

    const existing = newFilters.get(category);
    newFilters.set(category, {
      items: existing?.items || [],
      isLoading,
      error: existing?.error || null,
      itemsMap: existing?.itemsMap,
      pagination: existing?.pagination,
      isLoadingMore: existing?.isLoadingMore || false,
    });

    set(
      { dynamicFilters: newFilters },
      false,
      `[User] Dynamic Filters: Set loading state for ${category}`,
    );
  },

  updateDynamicItems: (category: string, items: DynamicFilterItem[]) => {
    const currentFilters = get().dynamicFilters;
    const newFilters = new Map(currentFilters);
    const normalizedItems = normalizeDynamicFilterItems(items);

    // Create items map for O(1) lookups
    const itemsMap = new Map(normalizedItems.map((item) => [item.id, item]));

    newFilters.set(category, {
      items: normalizedItems,
      itemsMap,
      isLoading: false,
      error: null,
      pagination: undefined, // No pagination for current implementation
      isLoadingMore: false,
    });

    set(
      { dynamicFilters: newFilters },
      false,
      `[User] Dynamic Filters: Update data for ${category}`,
    );
  },

  setDynamicError: (category: string, error: string | null) => {
    const currentFilters = get().dynamicFilters;
    const newFilters = new Map(currentFilters);

    const existing = newFilters.get(category);
    newFilters.set(category, {
      items: existing?.items || [],
      isLoading: false,
      error,
      itemsMap: existing?.itemsMap,
      pagination: existing?.pagination,
      isLoadingMore: existing?.isLoadingMore || false,
    });

    set(
      { dynamicFilters: newFilters },
      false,
      `[User] Dynamic Filters: Set error state for ${category}`,
    );
  },

  removeDynamicCategory: (category: string) => {
    const currentFilters = get().dynamicFilters;
    const newFilters = new Map(currentFilters);
    newFilters.delete(category);

    set(
      { dynamicFilters: newFilters },
      false,
      `[User] Dynamic Filters: Clear category ${category}`,
    );
  },

  clearAllDynamicFilters: () => {
    set(
      { dynamicFilters: new Map() },
      false,
      "[System] Dynamic Filters: Clear all dynamic filters",
    );
  },

  // Future pagination actions (placeholder implementations)
  setDynamicPage: undefined,
  appendDynamicItems: undefined,
  setDynamicLoadingMore: undefined,
});
